/**
 * Admin → Engagement Templates (renamed from "Business Setup Catalog", OPS 3.5) —
 * the engagement business-setup tree:
 * service → zone/jurisdiction → sub-item checklist, editable per zone. Engagement
 * business-setup sub-items are spawned from here.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import type { FreeZoneOutput, FreeZoneCatalogItemOutput } from '@1financial/shared';

async function apiError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string } };
    return new Error(body?.error?.message ?? `Failed: ${res.status}`);
  } catch {
    return new Error(text || res.statusText || `Failed: ${res.status}`);
  }
}

const HDRS = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` });

export function FreeZoneCatalogPage() {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const canManage = canDo('catalog.manage');

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [addingZone, setAddingZone] = useState(false);
  const [zoneCode, setZoneCode] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [itemService, setItemService] = useState('Business Setup');
  const [itemLabel, setItemLabel] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [deletingZone, setDeletingZone] = useState<FreeZoneOutput | null>(null);

  const list = useQuery({
    queryKey: ['free-zones', 'admin'],
    queryFn: async () => {
      const res = await fetch('/v1/free-zones?all=1', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
      const body = await res.json() as { ok: true; data: { items: FreeZoneOutput[] } };
      return body.data.items;
    },
  });

  const zone = (list.data ?? []).find((z) => z.id === selectedZoneId) ?? (list.data ?? [])[0] ?? null;

  const groupedItems = useMemo(() => {
    const groups = new Map<string, FreeZoneCatalogItemOutput[]>();
    for (const item of zone?.items ?? []) {
      const arr = groups.get(item.service) ?? [];
      arr.push(item);
      groups.set(item.service, arr);
    }
    return [...groups.entries()];
  }, [zone]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['free-zones'] });

  const createZone = useMutation({
    mutationFn: async () => {
      const res = await fetch('/v1/free-zones', { method: 'POST', headers: HDRS(), body: JSON.stringify({ code: zoneCode.trim().toUpperCase(), name: zoneName.trim() }) });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setAddingZone(false); setZoneCode(''); setZoneName(''); invalidate(); },
  });

  const updateZone = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const res = await fetch(`/v1/free-zones/${id}`, { method: 'PATCH', headers: HDRS(), body: JSON.stringify(patch) });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: invalidate,
  });

  const removeZone = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/free-zones/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setDeletingZone(null); setSelectedZoneId(null); invalidate(); },
  });

  const createItem = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/free-zones/${zone!.id}/items`, { method: 'POST', headers: HDRS(), body: JSON.stringify({ service: itemService.trim(), label: itemLabel.trim() }) });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setAddingItem(false); setItemLabel(''); invalidate(); },
  });

  const updateItem = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const res = await fetch(`/v1/free-zones/items/${id}`, { method: 'PATCH', headers: HDRS(), body: JSON.stringify(patch) });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => { setEditingItemId(null); invalidate(); },
  });

  const removeItem = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/free-zones/items/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: invalidate,
  });

  function moveItem(item: FreeZoneCatalogItemOutput, siblings: FreeZoneCatalogItemOutput[], dir: -1 | 1) {
    const idx = siblings.findIndex((x) => x.id === item.id);
    const swap = siblings[idx + dir];
    if (!swap) return;
    void updateItem.mutateAsync({ id: item.id, patch: { ordering: swap.ordering } })
      .then(() => updateItem.mutateAsync({ id: swap.id, patch: { ordering: item.ordering } }))
      .then(invalidate);
  }

  const mutationError = createZone.error ?? updateZone.error ?? removeZone.error ?? createItem.error ?? updateItem.error ?? removeItem.error;

  return (
    <div className="space-y-4 max-w-5xl">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Engagement Templates</h2>
          <p className="text-sm text-muted">Business-setup checklist per zone or jurisdiction — spawned onto engagements as one-off sub-items.</p>
        </div>
        {canManage && (
          <button onClick={() => setAddingZone(true)} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">
            + New zone
          </button>
        )}
      </header>

      {mutationError && <div className="text-sm text-rose-300">{mutationError.message}</div>}

      {addingZone && (
        <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col sm:flex-row gap-2">
          <input value={zoneCode} onChange={(e) => setZoneCode(e.target.value)} placeholder="CODE (e.g. SHAMS)" className="sm:w-48 bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm uppercase" />
          <input value={zoneName} onChange={(e) => setZoneName(e.target.value)} placeholder="Zone name" className="flex-1 bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
          <div className="flex gap-2">
            <button onClick={() => setAddingZone(false)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button
              onClick={() => createZone.mutate()}
              disabled={!zoneCode.trim() || !zoneName.trim() || createZone.isPending}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
            >
              Add zone
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Zone list */}
        <div className="w-full lg:w-64 shrink-0 bg-bg-panel border border-border rounded-xl p-2 space-y-1">
          {(list.data ?? []).map((z) => (
            <button
              key={z.id}
              onClick={() => setSelectedZoneId(z.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${zone?.id === z.id ? 'bg-bg-hover text-white' : 'text-muted hover:text-white hover:bg-bg-hover'} ${z.active ? '' : 'opacity-50'}`}
            >
              <span className="font-medium">{z.code}</span>
              <span className="block text-xs truncate">{z.name}</span>
            </button>
          ))}
        </div>

        {/* Zone detail */}
        {zone && (
          <div className="flex-1 min-w-0 w-full space-y-3">
            <div className="bg-bg-panel border border-border rounded-xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-white">{zone.code} — {zone.name}</div>
                <div className="text-xs text-muted">{zone.items.filter((i) => i.active).length} active checklist item(s)</div>
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <button
                    onClick={() => updateZone.mutate({ id: zone.id, patch: { active: !zone.active } })}
                    className="text-xs border border-border text-muted hover:text-white px-2 py-1 rounded"
                  >
                    {zone.active ? 'Deactivate zone' : 'Activate zone'}
                  </button>
                  <button onClick={() => setDeletingZone(zone)} className="text-xs btn-ghost-danger border px-2 py-1 rounded">Delete zone</button>
                </div>
              )}
            </div>

            {groupedItems.map(([service, items]) => (
              <div key={service} className="bg-bg-panel border border-border rounded-xl overflow-hidden">
                <div className="bg-bg-card border-b border-border px-4 py-2.5 text-sm font-medium text-white">{service}</div>
                <div className="divide-y divide-border">
                  {items.map((item, idx) => (
                    <div key={item.id} className={`flex items-center gap-2 px-4 py-2 ${item.active ? '' : 'opacity-50'}`}>
                      <span className="text-xs text-muted w-6">{idx + 1}.</span>
                      {editingItemId === item.id ? (
                        <>
                          <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="flex-1 bg-bg-card border border-border rounded-lg px-3 py-1.5 text-white text-sm" />
                          <button onClick={() => updateItem.mutate({ id: item.id, patch: { label: editLabel } })} disabled={!editLabel.trim()} className="text-xs btn-ghost-info border px-2 py-1 rounded disabled:opacity-50">Save</button>
                          <button onClick={() => setEditingItemId(null)} className="text-xs text-muted hover:text-white px-2 py-1">Cancel</button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm text-white">{item.label}{!item.active && <span className="ml-2 text-xs text-muted">(inactive)</span>}</span>
                          {canManage && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => moveItem(item, items, -1)} disabled={idx === 0} className="text-muted hover:text-white px-1 disabled:opacity-30" title="Move up">↑</button>
                              <button onClick={() => moveItem(item, items, 1)} disabled={idx === items.length - 1} className="text-muted hover:text-white px-1 disabled:opacity-30" title="Move down">↓</button>
                              <button onClick={() => { setEditingItemId(item.id); setEditLabel(item.label); }} className="text-xs btn-ghost-info border px-2 py-1 rounded">Edit</button>
                              <button onClick={() => updateItem.mutate({ id: item.id, patch: { active: !item.active } })} className="text-xs border border-border text-muted hover:text-white px-2 py-1 rounded">{item.active ? 'Deactivate' : 'Activate'}</button>
                              <button onClick={() => removeItem.mutate(item.id)} className="text-xs btn-ghost-danger border px-2 py-1 rounded">Delete</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {canManage && (
              addingItem ? (
                <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col sm:flex-row gap-2">
                  <input value={itemService} onChange={(e) => setItemService(e.target.value)} placeholder="Service group" className="sm:w-48 bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
                  <input value={itemLabel} onChange={(e) => setItemLabel(e.target.value)} placeholder="Checklist item label" className="flex-1 bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm" />
                  <div className="flex gap-2">
                    <button onClick={() => setAddingItem(false)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
                    <button
                      onClick={() => createItem.mutate()}
                      disabled={!itemService.trim() || !itemLabel.trim() || createItem.isPending}
                      className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
                    >
                      Add item
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAddingItem(true)} className="text-sm text-accent hover:underline">+ Add checklist item to {zone.code}</button>
              )
            )}
          </div>
        )}
      </div>

      {deletingZone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md p-5 shadow-2xl animate-pop">
            <h3 className="text-base font-semibold text-white mb-2">Delete free zone</h3>
            <p className="text-sm text-muted mb-4">Delete {deletingZone.code} and its {deletingZone.items.length} catalog item(s)? Sub-items already spawned on engagements are kept.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeletingZone(null)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
              <button onClick={() => removeZone.mutate(deletingZone.id)} disabled={removeZone.isPending} className="bg-rose-700 hover:bg-rose-600 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
